// Utilitários de validação reutilizáveis
export interface ValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

export interface ValidationRule<T = any> {
  validate: (value: T) => ValidationResult;
  required?: boolean;
}

// Função para obter descrição do tipo de usuário
export const getUserTypeDescription = (tipo: number): string => {
  switch (tipo) {
    case 0:
      return 'Usuário';
    case 1:
      return 'Empresa';
    case 2:
      return 'Administrador';
    default:
      return 'Desconhecido';
  }
};

// Validações específicas
export const emailValidation: ValidationRule<string> = {
  validate: (email: string): ValidationResult => {
    if (!email.trim()) {
      return { isValid: false, errorMessage: 'E-mail é obrigatório' };
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { isValid: false, errorMessage: 'Por favor, insira um e-mail válido' };
    }

    return { isValid: true };
  },
  required: true,
};

export const passwordValidation: ValidationRule<string> = {
  validate: (password: string): ValidationResult => {
    if (!password.trim()) {
      return { isValid: false, errorMessage: 'Senha é obrigatória' };
    }

    if (password.length < 6) {
      return { isValid: false, errorMessage: 'A senha deve ter pelo menos 6 caracteres' };
    }

    return { isValid: true };
  },
  required: true,
};

export const requiredValidation = (fieldName: string): ValidationRule<string> => ({
  validate: (value: string): ValidationResult => {
    if (!value.trim()) {
      return { isValid: false, errorMessage: `${fieldName} é obrigatório` };
    }
    return { isValid: true };
  },
  required: true,
});

export const minLengthValidation = (minLength: number, fieldName: string): ValidationRule<string> => ({
  validate: (value: string): ValidationResult => {
    if (value.length < minLength) {
      return { isValid: false, errorMessage: `${fieldName} deve ter pelo menos ${minLength} caracteres` };
    }
    return { isValid: true };
  },
});

export const maxLengthValidation = (maxLength: number, fieldName: string): ValidationRule<string> => ({
  validate: (value: string): ValidationResult => {
    if (value.length > maxLength) {
      return { isValid: false, errorMessage: `${fieldName} deve ter no máximo ${maxLength} caracteres` };
    }
    return { isValid: true };
  },
});

// Tipos para força da senha
export type PasswordStrength = 'very-weak' | 'weak' | 'fair' | 'good' | 'strong';

export interface PasswordStrengthResult {
  strength: PasswordStrength;
  score: number; // 0-100
  feedback: string[];
  isValid: boolean;
}

// Função para avaliar a força da senha
export const evaluatePasswordStrength = (password: string): PasswordStrengthResult => {
  let score = 0;
  const feedback: string[] = [];

  // Comprimento mínimo
  if (password.length === 0) {
    return {
      strength: 'very-weak',
      score: 0,
      feedback: ['Digite uma senha'],
      isValid: false
    };
  }

  if (password.length < 6) {
    feedback.push('A senha deve ter pelo menos 6 caracteres');
  } else {
    score += 20;
  }

  // Comprimento ideal
  if (password.length >= 8) {
    score += 10;
  } else if (password.length >= 6) {
    score += 5;
  }

  // Verificar se contém letras minúsculas
  if (/[a-z]/.test(password)) {
    score += 15;
  } else {
    feedback.push('Adicione letras minúsculas');
  }

  // Verificar se contém letras maiúsculas
  if (/[A-Z]/.test(password)) {
    score += 15;
  } else {
    feedback.push('Adicione letras maiúsculas');
  }

  // Verificar se contém números
  if (/\d/.test(password)) {
    score += 15;
  } else {
    feedback.push('Adicione números');
  }

  // Verificar se contém caracteres especiais
  if (/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    score += 15;
  } else {
    feedback.push('Adicione caracteres especiais (!@#$%^&*)');
  }

  // Penalizar senhas comuns
  const commonPasswords = ['123456', 'password', '123456789', 'qwerty', 'abc123', 'password123', 'admin', 'letmein'];
  if (commonPasswords.some(common => password.toLowerCase().includes(common))) {
    score = Math.max(0, score - 30);
    feedback.push('Evite senhas comuns');
  }

  // Penalizar sequências
  if (/(.)\1{2,}/.test(password)) {
    score = Math.max(0, score - 10);
    feedback.push('Evite caracteres repetidos');
  }

  // Penalizar sequências numéricas
  if (/123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz/.test(password.toLowerCase())) {
    score = Math.max(0, score - 10);
    feedback.push('Evite sequências');
  }

  // Garantir score mínimo/máximo
  score = Math.max(0, Math.min(100, score));

  // Determinar força baseada no score
  let strength: PasswordStrength;
  if (score < 20) strength = 'very-weak';
  else if (score < 40) strength = 'weak';
  else if (score < 60) strength = 'fair';
  else if (score < 80) strength = 'good';
  else strength = 'strong';

  // Se não há feedback negativo e score >= 60, considerar válida
  const isValid = score >= 60 && feedback.length === 0;

  return {
    strength,
    score,
    feedback,
    isValid
  };
};

// Validação de senha segura (para formulários)
export const securePasswordValidation: ValidationRule<string> = {
  validate: (password: string): ValidationResult => {
    const strengthResult = evaluatePasswordStrength(password);

    if (!strengthResult.isValid) {
      const errorMessage = strengthResult.feedback.length > 0
        ? strengthResult.feedback[0]
        : 'A senha deve ser mais forte';
      return { isValid: false, errorMessage };
    }

    return { isValid: true };
  },
  required: true,
};

// Validação de CPF/CNPJ (permite caracteres alfanuméricos)
export const cpfCnpjValidation: ValidationRule<string> = {
  validate: (value: string): ValidationResult => {
    if (!value.trim()) {
      return { isValid: false, errorMessage: 'CPF/CNPJ é obrigatório' };
    }

    // Remove caracteres não alfanuméricos para validação
    const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '');

    // Verifica se tem apenas caracteres alfanuméricos
    if (!/^[a-zA-Z0-9]+$/.test(cleanValue)) {
      return { isValid: false, errorMessage: 'CPF/CNPJ deve conter apenas letras e números' };
    }

    // Verifica se é CPF (11 dígitos apenas numéricos) ou CNPJ (14 caracteres alfanuméricos)
    if (cleanValue.length === 11 && /^\d+$/.test(cleanValue)) {
      return validateCPF(cleanValue);
    } else if (cleanValue.length === 14) {
      return validateCNPJ(cleanValue);
    } else {
      return { isValid: false, errorMessage: 'CPF deve ter 11 dígitos ou CNPJ deve ter 14 caracteres alfanuméricos' };
    }
  },
  required: true,
};

// Função para validar CPF
function validateCPF(cpf: string): ValidationResult {
  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cpf)) {
    return { isValid: false, errorMessage: 'CPF inválido' };
  }

  // Calcula primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(9))) {
    return { isValid: false, errorMessage: 'CPF inválido' };
  }

  // Calcula segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cpf.charAt(10))) {
    return { isValid: false, errorMessage: 'CPF inválido' };
  }

  return { isValid: true };
}

// Função para validar CNPJ (novo formato com caracteres alfanuméricos)
function validateCNPJ(cnpj: string): ValidationResult {
  // Verifica se tem exatamente 14 caracteres
  if (cnpj.length !== 14) {
    return { isValid: false, errorMessage: 'CNPJ deve ter exatamente 14 caracteres' };
  }

  // Verifica se as primeiras 12 posições são alfanuméricas
  if (!/^[a-zA-Z0-9]{12}/.test(cnpj.substring(0, 12))) {
    return { isValid: false, errorMessage: 'As primeiras 12 posições do CNPJ devem ser alfanuméricas' };
  }

  // Verifica se as últimas 2 posições são numéricas (dígitos verificadores)
  if (!/^\d{2}$/.test(cnpj.substring(12, 14))) {
    return { isValid: false, errorMessage: 'As últimas 2 posições do CNPJ devem ser numéricas (dígitos verificadores)' };
  }

  // Para validação dos dígitos verificadores, precisamos converter as letras para números
  // Usando uma tabela de conversão simples (A=10, B=11, ..., Z=35)
  const charToNumber = (char: string): number => {
    const code = char.toUpperCase().charCodeAt(0);
    if (code >= 65 && code <= 90) { // A-Z
      return 10 + (code - 65);
    }
    return parseInt(char); // 0-9
  };

  // Converte os primeiros 12 caracteres para números
  const digits: number[] = [];
  for (let i = 0; i < 12; i++) {
    digits.push(charToNumber(cnpj[i]));
  }

  // Adiciona os dígitos verificadores
  digits.push(parseInt(cnpj[12]));
  digits.push(parseInt(cnpj[13]));

  // Calcula primeiro dígito verificador
  let sum = 0;
  let weight = 5;
  for (let i = 0; i < 12; i++) {
    sum += digits[i] * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  let remainder = sum % 11;
  const firstVerifier = remainder < 2 ? 0 : 11 - remainder;

  if (firstVerifier !== digits[12]) {
    return { isValid: false, errorMessage: 'CNPJ inválido - primeiro dígito verificador incorreto' };
  }

  // Calcula segundo dígito verificador
  sum = 0;
  weight = 6;
  for (let i = 0; i < 13; i++) {
    sum += digits[i] * weight;
    weight = weight === 2 ? 9 : weight - 1;
  }
  remainder = sum % 11;
  const secondVerifier = remainder < 2 ? 0 : 11 - remainder;

  if (secondVerifier !== digits[13]) {
    return { isValid: false, errorMessage: 'CNPJ inválido - segundo dígito verificador incorreto' };
  }

  return { isValid: true };
}

// Função para aplicar máscara de CPF/CNPJ (suporta caracteres alfanuméricos)
export const applyCpfCnpjMask = (value: string): string => {
  // Remove caracteres não alfanuméricos para contar
  const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '');
  
  if (cleanValue.length <= 11) {
    // Máscara CPF: XXX.XXX.XXX-XX (onde X pode ser letra ou número)
    return cleanValue
      .replace(/([a-zA-Z0-9]{3})([a-zA-Z0-9])/, '$1.$2')
      .replace(/([a-zA-Z0-9]{3})\.([a-zA-Z0-9]{3})([a-zA-Z0-9])/, '$1.$2.$3')
      .replace(/([a-zA-Z0-9]{3})\.([a-zA-Z0-9]{3})\.([a-zA-Z0-9]{3})([a-zA-Z0-9]{1,2})$/, '$1.$2.$3-$4');
  } else {
    // Máscara CNPJ: XX.XXX.XXX/XXXX-XX (onde X pode ser letra ou número)
    return cleanValue
      .replace(/([a-zA-Z0-9]{2})([a-zA-Z0-9])/, '$1.$2')
      .replace(/([a-zA-Z0-9]{2})\.([a-zA-Z0-9]{3})([a-zA-Z0-9])/, '$1.$2.$3')
      .replace(/([a-zA-Z0-9]{2})\.([a-zA-Z0-9]{3})\.([a-zA-Z0-9]{3})([a-zA-Z0-9])/, '$1.$2.$3/$4')
      .replace(/([a-zA-Z0-9]{2})\.([a-zA-Z0-9]{3})\.([a-zA-Z0-9]{3})\/([a-zA-Z0-9]{4})([a-zA-Z0-9]{1,2})$/, '$1.$2.$3/$4-$5');
  }
};

// Validação de consulta SQL (apenas SELECT, sem modificações)
export const sqlQueryValidation: ValidationRule<string> = {
  validate: (sql: string): ValidationResult => {
    if (!sql.trim()) {
      return { isValid: false, errorMessage: 'Consulta SQL é obrigatória' };
    }

    const sqlUpper = sql.trim().toUpperCase();

    // Deve começar com SELECT
    if (!sqlUpper.startsWith('SELECT')) {
      return { isValid: false, errorMessage: 'Apenas consultas SELECT são permitidas' };
    }

    // Não permitir comandos de modificação - usar boundaries de palavra para evitar falsos positivos
    const forbiddenCommands = /\b(INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|TRUNCATE|EXEC|EXECUTE)\b/i;
    if (forbiddenCommands.test(sqlUpper)) {
      return { isValid: false, errorMessage: 'Comandos de modificação não são permitidos' };
    }

    // Não permitir comentários
    if (sqlUpper.includes('--') || sqlUpper.includes('/*') || sqlUpper.includes('*/')) {
      return { isValid: false, errorMessage: 'Comentários SQL não são permitidos' };
    }

    // Não permitir múltiplas consultas (mais de um ponto e vírgula)
    const semicolonCount = (sql.match(/;/g) || []).length;
    if (semicolonCount > 1) {
      return { isValid: false, errorMessage: 'Apenas uma consulta por vez é permitida' };
    }

    // Verificações básicas de SQL injection - mais permissivas
    const dangerousPatterns = [
      // Não bloquear UNION SELECT pois é válido em consultas SELECT
      // /(\bUNION\b.*\bSELECT\b)/i, // Removido - UNION é válido
      /(\bOR\b.*=.*[^-])/i, // OR com = mas não seguido de comentário
      /(\bAND\b.*=.*\s*--)/i, // AND com = seguido de comentário
      // Aspas simples são válidas em SQL, focar em padrões mais suspeitos
      /(\\x27\\x27|\\x2D\\x2D)/i, // Aspas duplas ou comentários hex
      /(\\\\x[0-9A-F]{2})/i, // Sequências hexadecimais suspeitas
      /(\bEXEC\b|\bEXECUTE\b)/i, // Execução de procedures
      /(\bXP_CMDSHELL\b)/i, // Shell commands
      /(\bSP_OA\b)/i, // OLE automation
      /(\\bSHUTDOWN\b|\\bBACKUP\b|\\bRESTORE\b)/i, // Comandos administrativos
    ];

    for (const pattern of dangerousPatterns) {
      if (pattern.test(sql)) {
        return { isValid: false, errorMessage: 'Consulta SQL contém padrões potencialmente perigosos' };
      }
    }

    return { isValid: true };
  },
  required: true,
};

// Tipos para o hook de validação
export interface FieldValidation {
  value: string;
  error: string;
  isValid: boolean;
  isDirty: boolean;
}

export interface ValidationState {
  [key: string]: FieldValidation;
}

// Função para validar um campo com múltiplas regras
export const validateField = (value: string, rules: ValidationRule[]): ValidationResult => {
  for (const rule of rules) {
    const result = rule.validate(value);
    if (!result.isValid) {
      return result;
    }
  }
  return { isValid: true };
};